import ky, { HTTPError, Options } from 'ky';

export class FetcherError extends Error {
    status: number;
    data: { detail: string };
    constructor(message: string, status: number, data: { detail: string }) {
        super(message);
        this.status = status;
        this.data = data;
        Error.captureStackTrace(this, FetcherError);
    }
}

export type FetcherOptions = Omit<Options, 'hooks'>;

const fetch = async <T>(url: string, options: FetcherOptions) => {
    try {
        return await ky<T>(url, {
            timeout: 20_000,
            ...options,
            hooks: {
                beforeRequest: [
                    (request) => {
                        request.headers.set('X-App-Version', import.meta.env.VITE_APP_VERSION ?? 'localhost');
                    },
                ],
            },
        }).json();
    } catch (error) {
        if (error instanceof HTTPError) {
            const statusText = error.response.statusText || `Request failed with status code ${error.response.status}`;
            const hasJsonResponse = error.response.headers.get('content-type')?.includes('application/json');
            if (hasJsonResponse) {
                const data = await error.response.json();
                throw new FetcherError(statusText, error.response.status, {
                    detail: data?.detail ?? statusText,
                });
            } else {
                throw new FetcherError(statusText, error.response.status, {
                    detail: statusText,
                });
            }
        }
        const err = error as Error;
        throw new FetcherError(err.message, 500, { detail: err.message });
    }
};

export const fetcher = {
    get: async <T>(url: string, options: FetcherOptions = {}) => {
        return await fetch<T>(url, { ...options, method: 'GET' });
    },
    post: async <T>(url: string, body: unknown, options: FetcherOptions = {}) => {
        const fetchOptions: FetcherOptions = {
            ...options,
            headers: {
                ...(options.headers ?? {}),
                'Content-Type': 'application/json',
            },
            method: 'POST',
            json: body,
        };
        return await fetch<T>(url, fetchOptions);
    },
    delete: async <T>(url: string, options: FetcherOptions = {}) => {
        return await fetch<T>(url, { ...options, method: 'DELETE' });
    },
};
